/** Settings > Integrations: the Zoho CRM card and the return from Zoho's consent screen. Merged into `settings.integrations.zoho`. */
export const settingsZoho = {
  name: "Zoho CRM",
  description: "New leads from your assistant are sent to Zoho CRM as Leads, so your sales team works them where they already work.",
  connect: "Connect Zoho CRM",
  reconnect: "Reconnect Zoho CRM",
  redirecting: "Redirecting to Zoho…",
  connectNote: "You'll sign in to Zoho and allow {{product}} to add and update Leads. It gets no access to your other Zoho CRM modules, and you can disconnect any time.",
  unavailable: "Ask the platform team to set up Zoho CRM.",
  notConfigured: "Zoho CRM isn't set up for this platform yet. Ask the platform team.",
  loadErrorTitle: "We couldn't load Zoho CRM",
  readOnly: "Ask an admin to manage this integration.",
  statusReconnect: "Reconnect needed",
  connectedBody: "New leads are sent to Zoho CRM as Leads.",
  reconnectBody: "Zoho no longer accepts {{product}}'s access (it may have been removed in Zoho). New leads are not being sent until you reconnect.",
  region: "Zoho region",
  regionAutomatic: "Automatic (recommended)",
  regionHint: "Automatic finds your account's data center when you sign in to Zoho. Choose a region only if you know yours.",
  details: {
    account: "Account email",
    organization: "Organization",
    datacenter: "Data center",
    lastSync: "Last sync",
    noSync: "No sync yet",
    syncResult: "{{date}} · {{synced}} synced, {{failed}} not accepted",
  },
  disconnect: {
    title: "Disconnect Zoho CRM?",
    body: "{{product}} stops sending leads and removes its access in Zoho. Leads already in Zoho stay there.",
    confirm: "Disconnect",
    done: "Zoho CRM disconnected",
  },
  returned: {
    connected: "Zoho CRM connected. New leads will be sent to Zoho.",
    reasons: {
      denied: "You didn't allow access in Zoho.",
      expired: "This connection attempt expired. Start again.",
      failed: "Zoho didn't accept the connection. Try again.",
      no_refresh: "Zoho did not grant lasting access. In Zoho > Settings > Connected Apps remove {{product}}, then connect again.",
      scope: "{{product}} needs permission to add leads in Zoho. Connect again and keep every box ticked.",
      unavailable: "Zoho CRM isn't set up for this platform yet. Ask the platform team.",
      forbidden: "You no longer have permission to manage integrations.",
    },
  },
  contactsSync: {
    "title": "Zoho contacts and accounts",
    "subtitle": "Sagot stays the support record; Zoho CRM stays the master.",
    "proposed": "Proposed",
    "tableCaption": "What is kept in step with Zoho CRM",
    "tableRecords": "Records",
    "tableDirection": "Direction",
    "tableLinked": "Linked",
    "tableStatus": "Status",
    "contactsRow": "Contacts",
    "contactsHint": "People, with Zoho Contacts",
    "accountsRow": "Accounts",
    "accountsHint": "Organisations, with Zoho Accounts",
    "linkedCount_one": "{{count}} linked",
    "linkedCount_other": "{{count}} linked",
    "deletedInZoho_one": "{{count}} deleted in Zoho",
    "deletedInZoho_other": "{{count}} deleted in Zoho",
    "statusOn": "Syncing",
    "statusOff": "Off",
    "switchLabel": "Sync contacts and accounts",
    "switchHint": "Zoho is read every 15 minutes and your changes are sent. A record deleted in Zoho is archived here, never removed.",
    "needsScopesTitle": "Reconnect to grant Contacts and Accounts access",
    "needsScopesBody": "Zoho CRM was connected for leads only. Reconnect it and keep the Contacts and Accounts boxes ticked so this sync can read and write them. Your lead sync is not affected.",
    "needsScopesButton": "Reconnect Zoho CRM",
    "needsScopesHint": "Reconnect Zoho CRM first (see above).",
    "needsZoho": "Connect Zoho CRM above first.",
    "directionLabel": "Direction",
    "rule": "The customer reference, tier and account status are only ever sent to Zoho: nothing in Zoho can change them here. When both sides changed a record, Zoho wins. Every change is in the audit log.",
    "mapping": "Field mapping",
    "syncNow": "Sync now",
    "syncing": "Syncing…",
    "noRun": "No sync yet.",
    "lastRun": "Last sync:",
    "runLine": "{{date}} · {{here}} written here, {{there}} sent to Zoho, {{failed}} not accepted",
    "saved": "Zoho sync settings saved",
    "ran": "Sync finished",
    "readOnly": "Ask an admin to manage this integration.",
    "loadErrorTitle": "We couldn't load the Zoho contacts sync",
    "directions": {
      "both": "Both ways",
      "from_zoho": "Zoho to Sagot",
      "to_zoho": "Sagot to Zoho"
    },
    "runCodes": {
      "not_connected": "Zoho CRM isn't connected. Connect it above.",
      "needs_reconnect": "Zoho no longer accepts the connection. Reconnect Zoho CRM above.",
      "scope": "Zoho has not allowed access to Contacts and Accounts. Reconnect Zoho CRM and allow them.",
      "unavailable": "Zoho CRM didn't answer. It will try again in a few minutes.",
      "busy": "A sync is already running. Try again in a moment.",
      "failed": "The sync stopped before it finished. It will try again in a few minutes."
    },
    "fields": {
      "contact_name": "Contact name",
      "contact_email": "Contact email",
      "contact_job_title": "Job title",
      "contact_organisation": "Organisation",
      "contact_customer_ref": "Customer reference",
      "contact_tier": "Customer tier",
      "contact_account_status": "Account status",
      "account_name": "Account name",
      "account_industry": "Industry",
      "account_notes": "Notes",
      "account_tier": "Account tier"
    },
    "mappingDialog": {
      "title": "Field mapping",
      "description": "Choose the Zoho field each Sagot field is kept in step with. Leave a field empty to leave it out.",
      "contactGroup": "Contacts",
      "accountGroup": "Accounts",
      "sendOnly": "Sent to Zoho only",
      "notMapped": "Not mapped",
      "problemName": "Use letters, digits and underscores, starting with a letter.",
      "problemDuplicate": "Another field already uses this Zoho field.",
      "referenceHint": "Fields marked “Sent to Zoho only” are never changed from Zoho. Mapping the customer reference needs permission to view customer references.",
      "reset": "Standard fields",
      "save": "Save mapping",
      "saving": "Saving…"
    }
  },
};
